// MATRIVON Market Intelligence Reports.
// Each report has publicly visible (crawlable) content plus a downloadable PDF.
// REPORTS_GATED must match backend server.py REPORTS_GATED. When false, PDFs download
// directly with no form; when true, a lead-capture form gates the download.
// block types supported by ArticleBlocks: p, h, list, quote, note, table
export const REPORTS_GATED = false;

export const REPORTS = [
  {
    slug: "gcc-polyurethane-market-update-september-2026",
    path: "/gcc-polyurethane-market-update-september-2026",
    category: "Market Update",
    region: "GCC · Saudi Arabia · UAE",
    period: "September 2026",
    title: "GCC Polyurethane Market Update – September 2026",
    subtitle:
      "Pricing Moves, Raw-Material Supply Risk and Localisation Trends across MDI, TDI and Polyols in Saudi Arabia, the UAE and the wider GCC",
    seoTitle: "GCC Polyurethane Market Update September 2026 | MDI TDI Polyol Prices | MATRIVON",
    metaDescription:
      "MATRIVON September 2026 GCC polyurethane market update: stable ~534 kt/y demand, coordinated MDI, TDI and polyol price increases, Sadara supply risk, European capacity restart and Saudi localisation.",
    excerpt:
      "Stable GCC demand meets a producer-led repricing cycle — coordinated MDI, TDI and polyol increases, continued Sadara supply risk, a European capacity restart and Saudi localisation momentum.",
    published: "2026-09-01",
    reviewed: "2026-09-01",
    readMins: 6,
    pdfFilename: "MATRIVON_GCC_Polyurethane_Market_Update_September_2026.pdf",
    image: "https://images.unsplash.com/photo-1739863305972-4eea3851e521?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "GCC polyurethane market update",
      "MDI price increase Middle East",
      "TDI price increase",
      "polyol price increase GCC",
      "Sadara polyurethane supply",
      "Saudi Arabia polyurethane localisation",
      "polyurethane market September 2026",
    ],
    intro:
      "This monthly MATRIVON market update tracks the GCC polyurethane market through September 2026. Demand remains broadly stable at approximately 534 kt/year, but the market has moved into a clear producer-led repricing cycle, with coordinated MDI, TDI and polyol price increases and continued regional supply risk.",
    headlineStats: [
      { value: "≈ 534 kt/yr", label: "GCC PU demand — broadly unchanged" },
      { value: "Firmer", label: "MDI, TDI & polyol pricing direction" },
      { value: "Elevated", label: "Regional supply risk (Sadara)" },
      { value: "+400/250 kt", label: "European MDI / TDI capacity restarted" },
    ],
    blocks: [
      { type: "h", text: "Executive signal" },
      {
        type: "p",
        text: "GCC polyurethane demand is stable at roughly 534 kt/year, but the supply and pricing environment has firmed materially. Coordinated producer price increases across MDI, TDI and polyols, combined with continued disruption at Sadara, have shifted the market from gradual firming into a clearer supplier repricing cycle. A European capacity restart adds supply but is unlikely to translate immediately into lower GCC transaction prices.",
      },
      { type: "h", text: "What changed versus the previous update" },
      {
        type: "list",
        items: [
          "Demand: broadly unchanged at ~534 kt/year across the GCC.",
          "Pricing: producer-led increases in MDI, TDI and polyols are accelerating — the market is materially firmer.",
          "Supply: Sadara's production disruption remains commercially important and keeps regional supply risk elevated.",
          "European supply: BorsodChem/Wanhua restarted ~400 kt/y MDI and ~250 kt/y TDI capacity, easing global tightness.",
          "Saudi Arabia: local stock, systems capacity and downstream focus continue to strengthen through localisation.",
        ],
      },
      { type: "h", text: "Raw-material pricing moves (August 2026 announcements)" },
      {
        type: "table",
        head: ["Producer", "Announced increase", "Notes"],
        rows: [
          ["Dow", "MDI +€200/t · Polyols +€250/t · PU systems +€150/t", "Effective late August 2026"],
          ["BorsodChem / Wanhua", "MDI +€200/t · TDI +€350/t · Polyols +€300/t", "Announced 18 August 2026"],
          ["Huntsman", "MDI +€250/t", "Europe, Africa & Middle East — effective 1 August 2026"],
        ],
      },
      {
        type: "note",
        text: "These are producer list-price announcements aimed at margin restoration, not concluded transaction prices. Realised GCC prices depend on negotiation, volumes and logistics, but the direction is clearly upward.",
      },
      { type: "h", text: "Sadara and regional supply risk" },
      {
        type: "p",
        text: "Sadara's prolonged production disruption continues to be the single most important regional supply variable, affecting availability and pricing across MDI, TDI and polyols. No reliable restart date should be assumed for planning. The practical response is resilience — qualified second sources, raw-material substitution and disciplined inventory — rather than forecasting a specific restart.",
      },
      { type: "h", text: "Country and application trends" },
      {
        type: "p",
        text: "Saudi Arabia remains the highest-priority market for growth and localisation, with continued momentum in rigid insulation, PIR and sandwich panels and cold-chain applications. The UAE stays central as a technical and logistics hub. Rigid insulation and spray foam remain the most strategically attractive application priorities for technically led suppliers.",
      },
      { type: "h", text: "Bottom line" },
      {
        type: "quote",
        text: "Demand is steady, but pricing and supply have entered a firmer, producer-led cycle — the advantage goes to buyers who have qualified alternatives and secured local stock in advance.",
      },
      {
        type: "note",
        text: "The full update includes the detailed pricing analysis, Sadara and capacity developments, country and application priorities, competitive/system-house implications and MATRIVON's sourcing and resilience recommendations.",
      },
    ],
    forWhom: [
      "Polyurethane raw-material producers and traders",
      "System houses and formulating processors",
      "Distributors and channel partners",
      "Sandwich-panel, cold-chain and insulation manufacturers",
      "Procurement and supply-chain leaders",
      "Technical and commercial decision-makers",
    ],
    related: [
      { label: "Polyurethane Consulting", to: "/polyurethane-consulting" },
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Supplier & Distributor Advisory", to: "/supplier-distributor-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
  {
    slug: "turkiye-polyurethane-market-update-september-2026",
    path: "/turkiye-polyurethane-market-update-september-2026",
    category: "Market Update",
    region: "Türkiye",
    period: "September 2026",
    title: "Türkiye Polyurethane Market Update – September 2026",
    subtitle:
      "Divergent Demand, Isocyanate Pricing Pressure and Intensifying System-House Competition across the Turkish Polyurethane Market",
    seoTitle: "Türkiye Polyurethane Market Update September 2026 | MDI TDI Polyol | MATRIVON",
    metaDescription:
      "MATRIVON September 2026 Türkiye polyurethane market update: ~630 kt/y demand, resilient rigid PU/PIR insulation, softer automotive and white goods, MDI/TDI/polyol pricing and system-house consolidation.",
    excerpt:
      "Turkish PU demand ~630 kt/y — resilient construction and insulation, but softer automotive and white goods, producer price pressure and intensifying domestic system-house competition.",
    published: "2026-09-01",
    reviewed: "2026-09-01",
    readMins: 6,
    pdfFilename: "MATRIVON_Turkiye_Polyurethane_Market_Update_September_2026.pdf",
    image: "https://images.unsplash.com/photo-1741275271810-897ed3359a0b?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "Turkey polyurethane market",
      "Türkiye polyurethane demand",
      "MDI TDI price Turkey",
      "polyol price Turkey",
      "Turkish system houses",
      "rigid PU PIR Turkey insulation",
    ],
    intro:
      "This monthly MATRIVON update tracks the Turkish polyurethane market through September 2026. Total demand is estimated at approximately 630 kt/year, but the market is diverging — construction and insulation stay resilient while automotive and white goods soften, and producers push isocyanate and polyol prices higher against real buyer resistance.",
    headlineStats: [
      { value: "≈ 630 kt/yr", label: "2026 Türkiye PU demand (600–660 kt)" },
      { value: "≈ 39%", label: "Rigid PU/PIR share (~245 kt)" },
      { value: "−8% / −15%", label: "Auto / white-goods output YoY" },
      { value: "Firmer", label: "MDI, TDI & polyol pricing" },
    ],
    blocks: [
      { type: "h", text: "Executive signal" },
      {
        type: "p",
        text: "Turkish polyurethane demand is holding at roughly 630 kt/year (range 600–660 kt), but the market is clearly two-track. Rigid PU/PIR foam for construction and insulation remains resilient, while automotive and white-goods weakness drags on flexible and appliance foams. Producers have announced coordinated MDI, TDI and polyol increases, yet soft downstream demand and improved regional isocyanate availability are tempering how much sticks.",
      },
      { type: "h", text: "Demand signals by end-use" },
      {
        type: "list",
        items: [
          "Construction & insulation: resilient — rigid PU/PIR foam is the largest application at ~39% of demand (~245 kt).",
          "Automotive: soft — production down ~8% YoY (Jan–Jul 2026), pressuring molded and flexible foams.",
          "White goods: weak — production down ~15% YoY, weighing on appliance rigid foam.",
          "Furniture & comfort: margin-sensitive flexible slabstock, exposed to FX and consumer demand.",
          "Coatings, adhesives & sealants (CASE): steadier specialty demand.",
        ],
      },
      { type: "h", text: "Pricing and supply" },
      {
        type: "p",
        text: "Wanhua, Huntsman, BorsodChem and Dow have announced MDI, TDI and polyol increases in the order of EUR 200–350/t. Buyer resistance is significant given weak end-markets, and BorsodChem's restart of ~400 kt/y MDI and ~250 kt/y TDI capacity has improved regional availability. Logistics risk persists — low Rhine water levels continue to threaten polyol supply chains.",
      },
      {
        type: "note",
        text: "These are producer list-price announcements, not concluded transactions. In a soft Turkish demand environment, realised prices depend heavily on volume, FX and negotiation.",
      },
      { type: "h", text: "System-house competition" },
      {
        type: "p",
        text: "Domestic system-house consolidation and capacity expansion — notably Kimpur's added capacity (~85 kt/y) and M&A activity — are intensifying competition for converters. Local formulation depth, service and technical credibility increasingly decide accounts over headline price.",
      },
      { type: "h", text: "Outlook (next 3–6 months)" },
      {
        type: "list",
        items: [
          "Rigid PU/PIR and sandwich panels: remain the strongest segment.",
          "Flexible slabstock: margin-sensitive, FX-exposed.",
          "Automotive and appliance foam: cautious until production recovers.",
          "Specialty CASE and export-oriented volumes: relatively resilient.",
        ],
      },
    ],
    forWhom: [
      "Polyurethane system houses and converters",
      "Raw-material producers and traders",
      "Sandwich-panel and insulation manufacturers",
      "Procurement and supply-chain leaders",
      "Investors tracking the Turkish chemicals market",
    ],
    related: [
      { label: "Polyurethane Consulting", to: "/polyurethane-consulting" },
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Market & Commercial Advisory", to: "/market-commercial-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
  {
    slug: "global-silicone-market-intelligence-september-2026",
    path: "/global-silicone-market-intelligence-september-2026",
    category: "Market Intelligence Report",
    region: "Global",
    period: "September 2026",
    title: "Global Silicone Market Intelligence – September 2026",
    subtitle:
      "A Two-Speed Market: Commodity Overcapacity, Specialty Growth, China's Role and the Applications Where Failure Is Expensive",
    seoTitle: "Global Silicone Market Intelligence September 2026 | Size, CAGR, Producers | MATRIVON",
    metaDescription:
      "MATRIVON September 2026 global silicone market intelligence: ~USD 25.8 bn / 3.32 Mt in 2026, ~5.3% CAGR to 2031, elastomers, specialty vs commodity, China DMC pricing and leading producers.",
    excerpt:
      "A two-speed global silicone market — ~USD 25.8 bn in 2026 growing ~5.3% to 2031, with commodity overcapacity from China alongside premium specialty growth in mobility, electronics, healthcare and energy.",
    published: "2026-09-01",
    reviewed: "2026-09-01",
    readMins: 9,
    pdfFilename: "MATRIVON_Global_Silicone_Market_Intelligence_September_2026.pdf",
    image: "https://images.unsplash.com/photo-1576141546153-3e04370b5ff7?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "global silicone market",
      "silicone market size 2026",
      "silicone elastomers market",
      "specialty silicone growth",
      "silicone DMC price China",
      "silicone producers Dow Shin-Etsu Wacker",
    ],
    intro:
      "This MATRIVON intelligence report assesses the global silicone market as of September 2026. It is a two-speed market: commodity chains face overcapacity and price volatility led by China, while specialty silicones command premium pricing and structural growth in mobility, electronics, healthcare and energy. The market is benchmarked at roughly USD 25.8 billion and 3.32 million tonnes in 2026.",
    headlineStats: [
      { value: "≈ USD 25.8 bn", label: "2026 global silicone market value" },
      { value: "≈ 3.32 Mt", label: "2026 global silicone volume" },
      { value: "≈ 5.3% CAGR", label: "To ≈ USD 33.5 bn by 2031" },
      { value: "Two-speed", label: "Commodity vs specialty divergence" },
    ],
    blocks: [
      { type: "h", text: "Executive intelligence" },
      {
        type: "p",
        text: "The global silicone market is worth approximately USD 25.8 billion (about 3.32 million tonnes) in 2026 and is projected to grow at roughly 5.3% value CAGR to around USD 33.5 billion and 4.26 million tonnes by 2031. The defining dynamic is divergence: commodity silicones face Chinese overcapacity and price volatility, while specialty grades — where failure is expensive — enjoy premium pricing and faster growth.",
      },
      { type: "h", text: "Market size and outlook" },
      {
        type: "table",
        head: ["Metric", "2026", "2031 (base)", "CAGR"],
        rows: [
          ["Value (USD bn)", "25.8", "33.5", "≈ 5.3%"],
          ["Volume (Mt)", "3.32", "4.26", "≈ 5.1%"],
        ],
      },
      { type: "h", text: "Product and application structure" },
      {
        type: "list",
        items: [
          "Elastomers (HCR, LSR, RTV): the largest physical form at ~49% of volume — construction, mobility, medical, electrical and industrial sealing.",
          "Fluids: broad process and personal-care use.",
          "Industrial processes: ~25% of application volume — antifoams, release agents, lubricants, coatings.",
          "Electronics / EV: high-growth — thermal management, potting, encapsulation.",
          "Healthcare: qualification-intensive, high value/kg, biocompatibility and documentation.",
          "Energy: growing PV, storage, grid and power-electronics demand.",
        ],
      },
      { type: "h", text: "Raw materials and pricing" },
      {
        type: "p",
        text: "China remains the marginal price-setter for many commodity chains. In late August 2026, Chinese DMC ran at ~63% utilisation quoting RMB 13,500–14,000/t — firmer but not tight. In contrast, European and Americas specialty prices rose 10–30% during 2026 on higher raw-material, energy and logistics costs, widening regional and grade-specific price dispersion.",
      },
      { type: "h", text: "Competitive landscape (indicative 2024 revenue share)" },
      {
        type: "table",
        head: ["Producer", "Share", "Producer", "Share"],
        rows: [
          ["Dow", "16.6%", "Hoshine", "3.5%"],
          ["Shin-Etsu", "11.6%", "Wynca", "3.5%"],
          ["WACKER", "9.9%", "Sika", "3.3%"],
          ["Sinochem", "5.7%", "Dongyue", "2.9%"],
          ["KCC", "4.2%", "Evonik", "2.7%"],
        ],
      },
      {
        type: "note",
        text: "This ranking mixes integrated upstream producers with downstream formulators and uses inconsistent denominators — treat as indicative. The full report includes the 2031 scenarios, strategic investment map and specialty growth analysis.",
      },
      { type: "h", text: "Strategic conclusions" },
      {
        type: "quote",
        text: "The pricing power sits where failure is expensive — batteries, medical devices, electronics and critical infrastructure. Win on application engineering and qualification, not commodity price.",
      },
    ],
    forWhom: [
      "Silicone producers and formulators",
      "Specialty chemical investors and strategists",
      "Electronics, EV, medical and energy OEMs",
      "Distributors and channel partners",
      "Procurement and sourcing leaders",
    ],
    related: [
      { label: "Silicone Consulting", to: "/silicone-consulting" },
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Market & Commercial Advisory", to: "/market-commercial-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
  {
    slug: "gcc-silicone-market-intelligence-september-2026",
    path: "/gcc-silicone-market-intelligence-september-2026",
    category: "Market Intelligence Report",
    region: "GCC · Saudi Arabia · UAE",
    period: "September 2026",
    title: "GCC Silicone Market Intelligence – September 2026",
    subtitle:
      "Import-Led Demand, the UAE Logistics Hub, Saudi Localisation and the Barbell Strategy for Commodity and Specialty Silicones",
    seoTitle: "GCC Silicone Market Intelligence September 2026 | Saudi Arabia & UAE | MATRIVON",
    metaDescription:
      "MATRIVON September 2026 GCC silicone market intelligence: ~50–60 kt demand, import dependence, UAE as logistics hub, Saudi localisation, construction and specialty applications and a barbell commercial strategy.",
    excerpt:
      "GCC silicone demand of ~50–60 kt is import-led — the UAE is the logistics hub and Saudi Arabia the growth engine, with value created through local formulation, technical service and a barbell portfolio.",
    published: "2026-09-01",
    reviewed: "2026-09-01",
    readMins: 8,
    pdfFilename: "MATRIVON_GCC_Silicone_Market_Intelligence_September_2026.pdf",
    image: "https://images.unsplash.com/photo-1604046412106-7f11740643fe?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "GCC silicone market",
      "Saudi Arabia silicone demand",
      "UAE silicone market",
      "silicone sealants construction GCC",
      "RTV silicone Middle East",
      "silicone localisation Saudi Arabia",
    ],
    intro:
      "This MATRIVON report assesses the GCC silicone market as of September 2026. The region has no upstream siloxane/DMC production, so demand of roughly 50–60 kt/year is met entirely by imports that are then distributed, compounded and formulated locally. The UAE is the logistics gateway; Saudi Arabia is the largest and fastest-growing end-use market and localisation focus.",
    headlineStats: [
      { value: "50–60 kt", label: "2026 GCC silicone demand (~55 kt mid)" },
      { value: "≈ 38.1 kt", label: "2024 primary-form flows (gross)" },
      { value: "Import-led", label: "No upstream siloxane/DMC in GCC" },
      { value: "Barbell", label: "Commodity + specialty strategy" },
    ],
    blocks: [
      { type: "h", text: "Executive summary" },
      {
        type: "p",
        text: "GCC silicone-material demand is estimated at 50–60 kt/year in 2026 (midpoint ~55 kt), structurally dependent on imported upstream material. The UAE (Jebel Ali/Dubai) is the dominant gateway for regional inventory and re-exports, while Saudi Arabia is the largest end-use growth engine with an accelerating localisation agenda. The commercial opportunity lies in local formulation, technical support and responsive inventory — not upstream production.",
      },
      { type: "h", text: "Country opportunity map" },
      {
        type: "table",
        head: ["Country", "Opportunity", "Primary themes"],
        rows: [
          ["Saudi Arabia", "Very high", "Largest growth engine; localisation; construction, oil & gas, RTV, power/solar, industrial, mobility"],
          ["United Arab Emirates", "High", "Regional logistics hub; construction, industrial, electronics, solar, personal care"],
          ["Oman", "Medium-high", "Specialty-heavy industrial — oil & gas, process aids, sealants"],
          ["Qatar", "Medium", "Project- and energy-driven — LNG/oil & gas, construction, electrical"],
          ["Kuwait", "Medium", "Import-led project & maintenance — construction, industrial, oil specialties"],
          ["Bahrain", "Niche", "Small, industrially concentrated — aluminium, construction, process"],
        ],
      },
      { type: "h", text: "Applications" },
      {
        type: "list",
        items: [
          "Construction sealants & adhesives: the largest volume driver, fed by major Saudi (SAR 29.5 bn awarded June 2026) and UAE (USD 59 bn in 2025) project pipelines.",
          "Oil & gas: specialty antifoams, high-temperature seals and process aids.",
          "Renewable energy (solar/power): junction-box sealing, potting and insulation.",
          "Industrial & electronics: encapsulation, thermal management and protection.",
          "Personal care & RTV: specialty fluids and room-temperature-vulcanising elastomers.",
        ],
      },
      { type: "h", text: "Import dependence and supply" },
      {
        type: "p",
        text: "The GCC lacks upstream siloxane/DMC capacity, so all base material is imported. China supplies a large share of commodity grades (roughly 20 kt to the UAE and ~4 kt to Saudi Arabia in the baseline year), keeping commodity pricing under pressure, while Europe supplies a higher-specialty mix. Landed cost, stock depth and approval lead times matter as much as ex-works price.",
      },
      {
        type: "note",
        text: "Hub throughput in the UAE should be distinguished from true domestic consumption. The full report includes trade-flow detail, demand scenarios and the local-supply strategy.",
      },
      { type: "h", text: "Strategic conclusions" },
      {
        type: "quote",
        text: "The winning local offer is a barbell — competitive commodity fluids and rubbers alongside a qualified specialty portfolio — backed by local stock, formulation and technical credibility.",
      },
    ],
    forWhom: [
      "Silicone producers, formulators and compounders",
      "Distributors and channel partners",
      "Construction, sealant and adhesive manufacturers",
      "Oil & gas, solar and industrial buyers",
      "Investors and business-development leaders",
    ],
    related: [
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Market & Commercial Advisory", to: "/market-commercial-advisory" },
      { label: "Supplier & Distributor Advisory", to: "/supplier-distributor-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
  {
    slug: "turkiye-silicone-market-intelligence-september-2026",
    path: "/turkiye-silicone-market-intelligence-september-2026",
    category: "Market Intelligence Report",
    region: "Türkiye",
    period: "September 2026",
    title: "Türkiye Silicone Market Intelligence – September 2026",
    subtitle:
      "Import Raw, Export Value: Türkiye as a Silicone Formulation and Re-Export Hub between China Volume and German Specialty",
    seoTitle: "Türkiye Silicone Market Intelligence September 2026 | Imports, Hub, Specialty | MATRIVON",
    metaDescription:
      "MATRIVON September 2026 Türkiye silicone market intelligence: ~48–56 kt demand, USD 270–360 m value, China-led import volume, German specialty value, export-hub role and specialty growth to 2031.",
    excerpt:
      "Türkiye silicone demand ~48–56 kt — a net importer and regional formulation/re-export hub: China leads volume, Germany leads value, with specialty growth the medium-term opportunity.",
    published: "2026-09-01",
    reviewed: "2026-09-01",
    readMins: 8,
    pdfFilename: "MATRIVON_Turkiye_Silicone_Market_Intelligence_September_2026.pdf",
    image: "https://images.unsplash.com/photo-1760998805344-39f14af44800?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "Turkey silicone market",
      "Türkiye silicone demand",
      "silicone imports Turkey China Germany",
      "Turkish silicone formulators",
      "silicone sealants Turkey",
      "specialty silicone Turkey",
    ],
    intro:
      "This MATRIVON report assesses the Turkish silicone market as of September 2026. Türkiye is a significant net importer of primary-form silicones and a regional formulation and re-export hub serving Europe, CIS, MENA and Central Asia. Broad silicone-material demand is estimated at approximately 48–56 kt/year (midpoint ~52 kt), worth roughly USD 270–360 million.",
    headlineStats: [
      { value: "48–56 kt", label: "2026 Türkiye silicone demand (~52 kt mid)" },
      { value: "USD 270–360 m", label: "2026 estimated market value" },
      { value: "55% / 36%", label: "China import volume / Germany value" },
      { value: "4.8–6.5% CAGR", label: "To ≈ 66–71 kt by 2031" },
    ],
    blocks: [
      { type: "h", text: "Executive summary" },
      {
        type: "p",
        text: "Türkiye consumes roughly 48–56 kt/year of silicone material (midpoint ~52 kt) and functions as an import-dependent formulation and re-export platform. In 2024 the net primary-form trade requirement was about 42.9 kt. Near-term end-use signals are soft, but strong local formulators, geographic access and export capability make the medium-term structurally attractive — especially as the mix shifts toward higher-value specialties.",
      },
      { type: "h", text: "Import sourcing (2024 baseline)" },
      {
        type: "table",
        head: ["Origin", "Import volume", "Import value", "Role"],
        rows: [
          ["China", "54.6%", "—", "Commodity / basic grades"],
          ["Germany", "—", "36.4%", "Higher specialty / qualified mix"],
          ["China + Germany", "~83%", "~75%", "Dominant combined sourcing"],
        ],
      },
      {
        type: "note",
        text: "The split — China leading volume, Germany leading value — reflects a deliberate commodity-vs-specialty sourcing strategy, and a concentration risk across Chinese cycles, European specialty pricing, FX and freight simultaneously.",
      },
      { type: "h", text: "Türkiye as a conversion and export platform" },
      {
        type: "p",
        text: "Local players formulate, package and redistribute imported silicones into finished goods for surrounding regions. Key names include Akkim/Akfix (a leading PU aerosol-foam and silicone-sealant producer, acquired by Sika in early 2026) and Selsil (exporting to 115+ countries), with Silstar acting as Bluestar/Elkem's distributor. The Sika–Akkim deal is likely to reshape the sealants landscape.",
      },
      { type: "h", text: "Applications and priority families" },
      {
        type: "list",
        items: [
          "Construction sealants: large but volume-pressured and price-sensitive; refurbishment and exports support demand.",
          "RTV-1 (neutral/weatherseal) and RTV-2 (moulding/potting): high-priority families.",
          "Elastomers: HCR and food/medical LSR under volume pressure, but EV/electronics grades structurally attractive.",
          "Release & coating silicones: priority specialty families.",
          "Textiles and personal care: FX- and demand-exposed specialty fluids and emulsions.",
        ],
      },
      { type: "h", text: "Outlook to 2031" },
      {
        type: "table",
        head: ["Scenario", "CAGR", "2031 demand", "Driver"],
        rows: [
          ["Base case", "≈ 4.8%", "≈ 66 kt", "Industrial normalisation + specialty growth"],
          ["High case", "≈ 6.5%", "≈ 71 kt", "Strong exports + faster specialty substitution"],
        ],
      },
      { type: "h", text: "Strategic conclusions" },
      {
        type: "quote",
        text: "The opportunity is to move the mix upward toward specialties and leverage Türkiye's export platform — not to chase commodity tonnage on price.",
      },
    ],
    forWhom: [
      "Silicone producers, formulators and distributors",
      "Sealant, adhesive and construction-chemical manufacturers",
      "Exporters serving CIS, MENA and Central Asia",
      "Procurement and sourcing leaders",
      "Investors tracking the Turkish chemicals market",
    ],
    related: [
      { label: "Silicone Consulting", to: "/silicone-consulting" },
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Market & Commercial Advisory", to: "/market-commercial-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
  {
    slug: "gcc-polyurethane-market-intelligence-2026",
    path: "/gcc-polyurethane-market-intelligence-2026",
    category: "Market Intelligence Report",
    region: "GCC · Saudi Arabia · UAE",
    period: "August 2026",
    title: "GCC Polyurethane Market Intelligence – August 2026",
    subtitle:
      "Market Size, Application Segments, Raw Material Dynamics, Competitive Landscape and Commercial Opportunities across Saudi Arabia, UAE and the GCC",
    seoTitle: "GCC Polyurethane Market Intelligence 2026 | Saudi Arabia & UAE | MATRIVON",
    metaDescription:
      "Independent GCC polyurethane market intelligence covering Saudi Arabia, UAE, PU demand, MDI, TDI, polyols, rigid insulation, system houses, supply dynamics and commercial opportunities.",
    excerpt:
      "An independent technical-commercial assessment of the GCC polyurethane market — demand, raw materials, system houses, applications, supply risks and commercial opportunities across Saudi Arabia, the UAE and the wider Gulf.",
    published: "2026-08-01",
    reviewed: "2026-08-31",
    readMins: 10,
    pdfFilename: "MATRIVON_GCC_Polyurethane_Market_Intelligence_August_2026.pdf",
    image: "https://images.unsplash.com/photo-1670689334024-ad61dd564fe2?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0",
    keywords: [
      "GCC polyurethane market",
      "Saudi Arabia polyurethane market",
      "UAE polyurethane market",
      "MDI Middle East",
      "TDI Middle East",
      "polyol market GCC",
      "rigid polyurethane insulation GCC",
      "PIR sandwich panels Middle East",
      "polyurethane system houses Middle East",
    ],
    intro:
      "This independent MATRIVON report provides a technical-commercial assessment of the GCC polyurethane market, prepared for industry decision-makers. It quantifies demand, maps the raw-material and system-house landscape, and identifies where the commercial opportunity actually sits. The 2026 base-case estimate points to approximately 534 kt/year (about USD 1.25 billion) of GCC polyurethane demand, with Saudi Arabia and the UAE together making up roughly two-thirds of the market.",
    headlineStats: [
      { value: "≈ 534 kt/yr", label: "2026 base-case GCC PU demand (≈ USD 1.25 bn)" },
      { value: "470–585 kt", label: "2026 sensitivity range" },
      { value: "≈ 4.3% CAGR", label: "Directional growth to ≈ 632 kt by 2030" },
      { value: "35% / 31%", label: "Saudi Arabia / UAE share of demand" },
    ],
    // Publicly visible content — enough for SEO and credibility, without reproducing the full report.
    blocks: [
      { type: "h", text: "Executive summary" },
      {
        type: "p",
        text: "The GCC polyurethane market is attractive but fragmented, driven by high insulation intensity, expanding industrial and logistics infrastructure and localised chemical production in Saudi Arabia. On a base case, 2026 demand is estimated at approximately 534 kt/year — worth around USD 1.25 billion at a weighted realised value of about USD 2.33/kg — within a sensitivity range of 470–585 kt/year, with a directional outlook of roughly 4.3% CAGR to about 632 kt by 2030. Flexible foam is the largest technology family, while rigid PUR/PIR insulation and spray foam are the most strategically attractive segments for technically led suppliers.",
      },
      {
        type: "p",
        text: "The central strategic message is that success in this market comes from application expertise and disciplined account selection — qualified alternative raw-material sourcing, formulation and process support, local inventory and technical troubleshooting — rather than commodity distribution on price.",
      },
      { type: "h", text: "Key market findings" },
      {
        type: "list",
        items: [
          "Estimated 2026 GCC polyurethane demand of approximately 534 kt/year (sensitivity range 470–585 kt), worth around USD 1.25 billion.",
          "Saudi Arabia accounts for roughly 35% of demand and the UAE roughly 31% — together about two-thirds of the GCC market.",
          "Flexible slabstock is the single largest application (~32%); rigid PUR/PIR panels (~20%) and spray foam (~19%) are the most strategically attractive.",
          "Saudi Arabia hosts world-scale MDI, TDI and polyether-polyol capacity that exceeds regional demand, making the market export-influenced.",
          "Supply volatility (e.g. the Sadara outage) and price moves reinforce the value of dual-sourcing, local stock and technical assurance.",
        ],
      },
      { type: "h", text: "GCC demand by country (2026 base case)" },
      {
        type: "table",
        head: ["Country", "Demand (kt)", "Share", "Primary themes"],
        rows: [
          ["Saudi Arabia", "185", "34.6%", "Rigid panels, cold chain, comfort foam, industrial, automotive localisation"],
          ["United Arab Emirates", "163", "30.6%", "Panels & cold rooms, logistics, construction, CASE, re-export"],
          ["Qatar", "69", "13.0%", "Construction, cold chain, HVAC/insulation, infrastructure"],
          ["Oman", "57", "10.7%", "Industrial insulation, cold rooms, spray foam"],
          ["Kuwait", "31", "5.8%", "Construction, refrigeration, comfort foam"],
          ["Bahrain", "29", "5.4%", "Construction, industrial maintenance, comfort foam, logistics"],
          ["GCC total", "534", "100%", "Sensitivity range: 470–585 kt/year"],
        ],
      },
      { type: "h", text: "Demand by technology and application" },
      {
        type: "p",
        text: "Flexible foam is the largest technology family in the GCC, led by slabstock for bedding, furniture and cushioning. Rigid PUR/PIR panels and spray foam are smaller by volume but the most strategically attractive, because insulation performance and tightening energy and fire specifications make them technically demanding and less price-driven.",
      },
      {
        type: "table",
        head: ["Segment", "Demand (kt)", "Share", "Commercial profile"],
        rows: [
          ["Flexible slabstock (conventional, HR, viscoelastic)", "170", "31.8%", "High volume, price-sensitive"],
          ["Rigid PUR/PIR panels, cold rooms & laminates", "105", "19.7%", "High strategic attractiveness"],
          ["Spray PU foam", "101", "18.9%", "High technical value"],
          ["Molded flexible / cold-cure HR", "58", "10.9%", "Qualification-intensive"],
          ["Rigid boards, pipe & industrial insulation", "44", "8.2%", "Specification-driven"],
          ["Rigid appliance / refrigeration", "42", "7.9%", "Qualification-intensive"],
          ["Integral skin / semi-rigid / microcellular", "14", "2.6%", "Niche, high technical content"],
        ],
      },
      { type: "h", text: "Saudi Arabia and UAE market positioning" },
      {
        type: "p",
        text: "Saudi Arabia is both the largest demand pool and the region's only source of world-scale integrated MDI, TDI and polyether-polyol capacity, giving it an export orientation that shapes regional availability and pricing. The UAE functions as the region's technical and logistics hub, concentrating system houses, panel manufacturers and converters around Dubai and Sharjah and serving as the natural entry point for regional trading and technical-support models.",
      },
      { type: "h", text: "Raw materials: MDI, TDI and polyols" },
      {
        type: "p",
        text: "Most reactive raw materials in the region trace back to large-scale Saudi capacity that exceeds modelled GCC demand, so local availability is linked to operating rates and export allocation rather than to domestic demand alone. Modelled GCC input demand is on the order of 175 kt/year MDI/PMDI and 62 kt/year TDI, alongside roughly 193 kt/year base polyether polyols plus about 56 kt polyester and 27 kt polymer/specialty polyols, supplemented by meaningful Asian imports. Technical equivalence across multiple isocyanate grades and polyol sources is therefore a genuine commercial lever.",
      },
      {
        type: "quote",
        text: "Security of supply has economic value: customers will pay for a technically approved second source, local inventory and shorter lead times.",
      },
      { type: "h", text: "Strategic conclusions" },
      {
        type: "list",
        items: [
          "Compete on technical value and supply-risk reduction, not commodity price.",
          "Maintain at least two qualified isocyanate origins and treat China as a strategic alternative, not merely the cheapest.",
          "Target mid-sized sandwich-panel, cold-room and spray-foam converters where technical service is decisive.",
          "Use the UAE as the technical and logistics base for regional coverage.",
        ],
      },
      {
        type: "note",
        text: "This page summarises selected findings. The full report includes the demand model and assumptions, the raw-material and system-house landscape, the 2026 supply-risk and pricing environment, a go-to-market opportunity map and a 90-day commercial intelligence plan.",
      },
    ],
    forWhom: [
      "Polyurethane raw-material producers and traders",
      "System houses and formulating processors",
      "Distributors and channel partners",
      "Sandwich-panel, cold-chain and insulation manufacturers",
      "Investors and strategic buyers",
      "Technical and commercial decision-makers",
    ],
    related: [
      { label: "Polyurethane Consulting", to: "/polyurethane-consulting" },
      { label: "Technical Intelligence", to: "/technical-intelligence" },
      { label: "Market & Commercial Advisory", to: "/market-commercial-advisory" },
      { label: "Contact MATRIVON", to: "/contact" },
    ],
  },
];

export function getReport(slug) {
  return REPORTS.find((r) => r.slug === slug);
}
